import type { FilterCriteria, Page, UserDto } from '@ampsec/platform-client';
import { getGridData } from 'Apis/library';
import type { IServerSideDatasource } from 'ag-grid-community';
import { useCallback } from 'react';
import type { UrlParams } from '../AgGridBase';
import {
  type ServerDatasourceBuildContext,
  buildOffsetLimit,
  buildSingleSort,
  pickSetFilterValuesFromModel,
  readTrimmedUrlParam,
  useServerSideDatasource,
} from '../AgGridBase';
import { usersOrgFilterFromOrgModel } from 'Core-utils/helpers/orgModelFilters';
import { USERS_GRID_CONFIG } from './usersGrid.config';
import { mapUserDtosToEmployees } from './usersTable';

function buildDeterministicSort(
  sortModel: ServerDatasourceBuildContext<UrlParams, Record<string, unknown>>['sortModel']
): Record<string, 'ASC' | 'DESC'> {
  const primarySort = buildSingleSort(sortModel) ?? { score: 'ASC' as const };
  const primaryField = Object.keys(primarySort)[0];
  const primaryDirection = primarySort[primaryField] ?? 'ASC';
  if (primaryField === 'id') {
    return primarySort;
  }
  // Add a stable tiebreaker so offset-based pagination doesn't shift blocks
  // when many rows share the same primary sort value (e.g. score ties).
  return {
    ...primarySort,
    id: primaryDirection,
  };
}

export function useUsersServerSideDatasource(
  urlFilter: UrlParams,
  globalOrganization: string
): IServerSideDatasource {
  const buildQuery = useCallback(
    ({
      startRow,
      endRow,
      sortModel,
      filterModel,
      urlFilter: incomingUrlFilter,
    }: ServerDatasourceBuildContext<UrlParams, Record<string, unknown>>) => {
      const { offset, limit } = buildOffsetLimit(
        startRow,
        endRow ?? startRow + USERS_GRID_CONFIG.cacheBlockSize
      );
      const sort = buildDeterministicSort(sortModel);
      const setFilters = pickSetFilterValuesFromModel(filterModel, ['department', 'organization', 'title']);
      const q = readTrimmedUrlParam(incomingUrlFilter, 'q');

      return {
        offset,
        limit,
        sort,
        ...usersOrgFilterFromOrgModel(globalOrganization),
        ...(setFilters as Partial<FilterCriteria>),
        ...(q ? { q } : {}),
      } satisfies FilterCriteria;
    },
    [globalOrganization]
  );

  const fetchRows = useCallback(async (query: FilterCriteria) => {
    const response = (await getGridData('users', query)) as Page<UserDto>;
    const rows = mapUserDtosToEmployees(response?.data);
    return {
      rows,
      total: response?.hints?.count ?? rows.length,
    };
  }, []);

  return useServerSideDatasource<
    FilterCriteria,
    ReturnType<typeof mapUserDtosToEmployees>[number],
    UrlParams,
    Record<string, unknown>
  >({
    urlFilter,
    buildQuery,
    fetchRows,
  });
}
